import React from 'react';
import { Box, Button, Typography, styled, keyframes } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fef2f2',
  backgroundImage: `radial-gradient(circle at 25% 25%, rgba(244, 67, 54, 0.05) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(244, 67, 54, 0.05) 0%, transparent 50%)`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    pointerEvents: 'none'
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: '0 16px',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
  animation: `${fadeIn} 0.8s ease-out`,
  [theme.breakpoints.up('sm')]: {
    padding: '0 24px'
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 600
  }
}));

const IconContainer = styled(Box)({
  marginBottom: 24,
  animation: `${fadeIn} 0.8s ease-out 0.2s both`
});

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#dc2626',
  marginBottom: 8,
  animation: `${fadeIn} 0.8s ease-out 0.4s both`,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem'
  }
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#475569',
  marginBottom: 16,
  animation: `${fadeIn} 0.8s ease-out 0.6s both`
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#64748b',
  marginBottom: 32,
  lineHeight: 1.6,
  animation: `${fadeIn} 0.8s ease-out 0.8s both`,
  maxWidth: 400,
  margin: '0 auto 32px'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.95rem',
  minWidth: 120,
  transition: 'all 0.2s ease',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  color: '#374151',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  animation: `${fadeIn} 0.8s ease-out 1s both`
}));

export default function PaymentCancel() {
  const navigate = useNavigate();

  return (
    <StyledContainer>
      <ContentContainer>
        <IconContainer>
          <Cancel
            sx={{
              fontSize: '64px',
              color: '#dc2626'
            }}
          />
        </IconContainer>

        <TitleText variant="h4">Payment Cancelled</TitleText>

        <SubtitleText variant="h6">No worries, you can try again anytime</SubtitleText>

        <DescriptionText variant="body1">
          Your payment was cancelled and no charges were made to your account. If you have any
          questions or need assistance with your subscription, our support team is here to help.
        </DescriptionText>

        <StyledButton onClick={() => navigate('/pricing')}>View Pricing Plans</StyledButton>
      </ContentContainer>
    </StyledContainer>
  );
}
